import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { PROVIDER_HAS_PORTAL, PROVIDER_LABELS, PROVIDER_METHODS } from '@shared/payments'
import { api, ApiError, type WorkspaceResponse } from '../lib/api'
import { openRazorpayCheckout } from '../lib/razorpayCheckout'
import { money } from './PricingConfigurator'
import { Banner, Button, Card, Chip } from '../ui'

const REGION_NAMES =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null

function countryName(code: string | null): string {
  if (!code) return 'your region'
  try {
    return REGION_NAMES?.of(code) ?? code
  } catch {
    return code
  }
}

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : null

export function CheckoutPanel({
  data,
  hasUnsavedChanges = false,
}: {
  data: WorkspaceResponse
  /** Edits not yet saved price differently from the subscription we would actually charge. */
  hasUnsavedChanges?: boolean
}) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'idle' | 'cancelled' | 'paid'>('idle')
  const [confirmingCancel, setConfirmingCancel] = useState(false)

  const subscription = data.subscription
  const billing = data.billing
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['workspace'] })

  const checkout = useMutation({
    mutationFn: async () => {
      setStatus('idle')
      const session = await api.checkout()

      if (session.provider === 'stripe') {
        // Stripe takes over the page; nothing after this runs on success.
        window.location.href = session.checkoutUrl
        return 'redirecting' as const
      }

      const result = await openRazorpayCheckout({
        keyId: session.keyId,
        subscriptionId: session.subscriptionId,
        name: session.name,
        description: session.description,
        prefill: session.prefill,
      })
      if (!result) return 'cancelled' as const

      await api.verifyRazorpay({
        subscriptionId: result.razorpay_subscription_id,
        paymentId: result.razorpay_payment_id,
        signature: result.razorpay_signature,
      })
      return 'paid' as const
    },
    onSuccess: async (outcome) => {
      if (outcome === 'cancelled') setStatus('cancelled')
      if (outcome === 'paid') {
        setStatus('paid')
        await refresh()
      }
    },
  })

  const cancel = useMutation({
    mutationFn: api.cancelSubscription,
    onSuccess: async () => {
      setConfirmingCancel(false)
      await refresh()
    },
  })

  const resume = useMutation({ mutationFn: api.resumeSubscription, onSuccess: refresh })

  const portal = useMutation({
    mutationFn: api.billingPortal,
    onSuccess: ({ url }) => {
      window.location.href = url
    },
  })

  if (!subscription || !data.workspace) return null

  const provider = billing.provider
  const isPastDue = subscription.status === 'past_due'
  /**
   * Existence of a schedule at the processor — not whether the last charge succeeded.
   * Branching on payment status would offer "Subscribe" to a past-due customer who already
   * has a live mandate, creating a second subscription and billing them twice.
   */
  const hasSubscription = Boolean(subscription.providerSubscriptionId)
  const isCanceling = subscription.cancelAtPeriodEnd
  const renewsOn = formatDate(subscription.currentPeriodEnd)
  const trialEnds = formatDate(subscription.trialEndsAt)

  const cycleMinor =
    subscription.billingCycle === 'annual'
      ? (data.quote?.total ?? 0) * 12
      : (data.quote?.total ?? 0)
  const amountLabel = data.quote
    ? money(cycleMinor, data.quote.currency, data.quote.minorUnits, data.workspace.localeId)
    : '—'
  const cadence = subscription.billingCycle === 'annual' ? 'year' : 'month'

  const regionBlocked = !billing.regionOk
  // No card processor wired yet. The checkout route fails closed with a 503, so rather than offer a
  // Subscribe button that can only error, we show a calm note. It clears the instant keys are set.
  const billingClosed = !billing.enabled
  const busyError = [checkout.error, cancel.error, resume.error, portal.error].find(Boolean)
  const errorMessage =
    busyError instanceof ApiError || busyError instanceof Error ? busyError.message : undefined

  return (
    <Card variant="outlined">
      <div className="stack gap-4">
        <div className="row between wrap gap-3">
          <h2 className="cad-section-title">Subscription</h2>
          {isPastDue ? (
            <Chip tone="error">Payment failed</Chip>
          ) : isCanceling ? (
            <Chip tone="warn">Ending {renewsOn}</Chip>
          ) : hasSubscription ? (
            <Chip tone="success">Active</Chip>
          ) : (
            <Chip tone="warn">Trial · not started</Chip>
          )}
        </div>

        {hasSubscription ? (
          <>
            <div className="stack gap-2">
              <div className="row between gap-3 wrap">
                <span className="cad-muted">
                  {isCanceling ? 'Access until' : `Renews every ${cadence}`}
                </span>
                <span className="headline-small tabular">{amountLabel}</span>
              </div>
              <p className="cad-muted">
                {isCanceling
                  ? `Cancelled. Your workspace stays active until ${renewsOn}, and you will not be charged again.`
                  : `Next charge ${renewsOn ?? 'at the end of this period'} · ${PROVIDER_LABELS[provider]}`}
              </p>
            </div>

            {isPastDue && (
              <Banner tone="error">
                {PROVIDER_HAS_PORTAL[provider]
                  ? `We could not collect your last payment. Update your card below to keep the workspace active — ${PROVIDER_LABELS[provider]} will retry over the next few days.`
                  : `We could not collect your last payment. ${PROVIDER_LABELS[provider]} will email you a secure link to retry, and will keep trying over the next few days. Your workspace stays active in the meantime.`}
              </Banner>
            )}
            {errorMessage && <Banner tone="error">{errorMessage}</Banner>}

            <div className="row gap-2 wrap">
              {PROVIDER_HAS_PORTAL[provider] && (
                <Button
                  variant="tonal"
                  disabled={portal.isPending}
                  onClick={() => portal.mutate()}
                >
                  {portal.isPending ? 'Opening…' : 'Manage payment method'}
                </Button>
              )}
              {isCanceling ? (
                billing.canResume ? (
                  <Button
                    variant="tonal"
                    disabled={resume.isPending}
                    onClick={() => resume.mutate()}
                  >
                    {resume.isPending ? 'Resuming…' : 'Resume subscription'}
                  </Button>
                ) : (
                  <span className="cad-muted">
                    {PROVIDER_LABELS[provider]} cannot reverse a cancellation. You can start a new
                    subscription once this period ends.
                  </span>
                )
              ) : (
                <Button
                  variant="text"
                  disabled={cancel.isPending}
                  onClick={() => setConfirmingCancel(true)}
                >
                  Cancel subscription
                </Button>
              )}
            </div>

            {confirmingCancel && (
              <Banner tone="warn">
                <div className="stack gap-3">
                  <span>
                    Cancel your subscription? Your workspace stays active until {renewsOn}.
                    {!billing.canResume &&
                      ` ${PROVIDER_LABELS[provider]} cannot undo this — restarting means a new subscription.`}
                  </span>
                  <div className="row gap-2 wrap">
                    <Button
                      variant="filled"
                      disabled={cancel.isPending}
                      onClick={() => cancel.mutate()}
                    >
                      {cancel.isPending ? 'Cancelling…' : 'Yes, cancel'}
                    </Button>
                    <Button variant="text" onClick={() => setConfirmingCancel(false)}>
                      Keep subscription
                    </Button>
                  </div>
                </div>
              </Banner>
            )}
          </>
        ) : (
          <>
            <div className="stack gap-2">
              <div className="row between gap-3 wrap">
                <span className="cad-muted">Then {amountLabel} per {cadence}</span>
                <span className="headline-small tabular">{amountLabel}</span>
              </div>
              <p className="cad-muted">
                {trialEnds
                  ? `Nothing is charged until your trial ends on ${trialEnds}. `
                  : ''}
                Billing from {countryName(billing.country)} · {PROVIDER_LABELS[provider]}
              </p>
            </div>

            <div className="row gap-2 wrap">
              {PROVIDER_METHODS[provider].map((method) => (
                <Chip key={method} tone="assist">
                  {method}
                </Chip>
              ))}
            </div>

            {hasUnsavedChanges && (
              <Banner tone="warn">
                This subscription is for your saved plan. Save your changes first if you want to
                subscribe to the configuration above.
              </Banner>
            )}
            {regionBlocked && !billingClosed && (
              <Banner tone="error">{billing.regionReason}</Banner>
            )}
            {status === 'cancelled' && (
              <Banner tone="info">Checkout closed. Nothing was charged.</Banner>
            )}
            {status === 'paid' && <Banner tone="success">Subscription active. Thank you.</Banner>}
            {errorMessage && <Banner tone="error">{errorMessage}</Banner>}

            {billingClosed ? (
              <>
                <Banner tone="info">
                  Card payments are being switched on for this workspace. Your trial keeps running
                  {trialEnds ? ` until ${trialEnds}` : ''}, and you will be able to subscribe here
                  as soon as it is live — nothing is lost in the meantime.
                </Banner>
                <Button size="large" full disabled>
                  Billing opens soon
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="large"
                  full
                  disabled={checkout.isPending || regionBlocked || hasUnsavedChanges}
                  onClick={() => checkout.mutate()}
                >
                  {checkout.isPending
                    ? `Opening ${PROVIDER_LABELS[provider]}…`
                    : trialEnds
                      ? 'Set up billing'
                      : `Subscribe · ${amountLabel}/${cadence}`}
                </Button>

                <p className="cad-muted" style={{ textAlign: 'center' }}>
                  Card details are entered on {PROVIDER_LABELS[provider]} and never reach our
                  servers. Cancel anytime.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </Card>
  )
}

export function InvoiceHistory({ data }: { data: WorkspaceResponse }) {
  if (data.invoices.length === 0 || !data.workspace) return null
  const localeId = data.workspace.localeId

  return (
    <Card variant="outlined">
      <div className="stack gap-3">
        <h2 className="cad-section-title">Billing history</h2>
        <div className="cad-grid-frame">
          <table className="cad-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Period</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{formatDate(invoice.createdAt)}</td>
                  <td className="cad-muted">
                    {invoice.periodStart && invoice.periodEnd
                      ? `${formatDate(invoice.periodStart)} – ${formatDate(invoice.periodEnd)}`
                      : '—'}
                  </td>
                  <td className="tabular" style={{ textAlign: 'right' }}>
                    {money(invoice.amountMinor, invoice.currency, 100, localeId)}
                  </td>
                  <td>
                    <Chip tone={invoice.status === 'paid' ? 'success' : 'error'}>
                      {invoice.status === 'paid' ? 'Paid' : 'Failed'}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  )
}
